import type { NetworkEntry } from '../types'

const BODY_LIMIT = 4000

function sanitizeUrl(input: string): string {
  try {
    const url = new URL(input, window.location.href)
    for (const key of Array.from(url.searchParams.keys())) {
      if (/token|key|secret|password|auth|session/i.test(key)) {
        url.searchParams.set(key, '[redacted]')
      }
    }
    return url.toString()
  } catch {
    return input
  }
}

function methodFromInput(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) return init.method.toUpperCase()
  if (input instanceof Request) return input.method.toUpperCase()
  return 'GET'
}

function urlFromInput(input: RequestInfo | URL): string {
  if (input instanceof Request) return input.url
  return String(input)
}

function redactValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(redactValue)
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
      out[key] = /token|key|secret|password|auth|session/i.test(key) ? '[redacted]' : redactValue(entry)
    }
    return out
  }
  return value
}

function truncateBody(body: string): string {
  return body.length > BODY_LIMIT ? `${body.slice(0, BODY_LIMIT)}…[truncated]` : body
}

function stringifyBody(value: unknown): string | undefined {
  if (value == null) return undefined
  if (typeof value === 'string') {
    try {
      return truncateBody(JSON.stringify(redactValue(JSON.parse(value)), null, 2))
    } catch {
      return truncateBody(value)
    }
  }
  if (value instanceof URLSearchParams) return truncateBody(value.toString())
  if (value instanceof FormData) {
    const entries = Array.from(value.entries()).map(([key, entry]) => [key, typeof entry === 'string' ? entry : `[${entry.type || 'blob'}:${entry.name || 'file'}]`])
    return truncateBody(JSON.stringify(redactValue(Object.fromEntries(entries)), null, 2))
  }
  if (typeof Blob !== 'undefined' && value instanceof Blob) return `[blob:${value.type || 'application/octet-stream'}:${value.size} bytes]`
  if (value instanceof Document) return `[document:${value.URL || 'unknown'}]`
  if (typeof value === 'object') return truncateBody(JSON.stringify(redactValue(value), null, 2))
  return truncateBody(String(value))
}

async function requestBodyFromFetch(input: RequestInfo | URL, init?: RequestInit): Promise<string | undefined> {
  if (init?.body !== undefined) return stringifyBody(init.body)
  if (input instanceof Request) {
    try {
      return stringifyBody(await input.clone().text())
    } catch {
      return undefined
    }
  }
  return undefined
}

async function responseBodyFromFetch(response: Response): Promise<string | undefined> {
  try {
    return stringifyBody(await response.clone().text())
  } catch {
    return undefined
  }
}

function shouldCaptureBodies(method: string, status?: number, error?: string): boolean {
  if (method === 'POST') return true
  if (error) return true
  return (status ?? 0) >= 400
}

export class NetworkCapture {
  private entries: NetworkEntry[] = []
  private originalFetch: typeof window.fetch | null = null
  private originalSendBeacon: typeof navigator.sendBeacon | null = null
  private originalOpen: typeof XMLHttpRequest.prototype.open | null = null
  private originalSend: typeof XMLHttpRequest.prototype.send | null = null

  constructor(private readonly endpoint = '/visual-qa', private readonly limit = 80) {}

  start(): void {
    this.patchFetch()
    this.patchSendBeacon()
    this.patchXhr()
  }

  stop(): void {
    if (this.originalFetch) window.fetch = this.originalFetch
    if (this.originalSendBeacon) navigator.sendBeacon = this.originalSendBeacon
    if (this.originalOpen) XMLHttpRequest.prototype.open = this.originalOpen
    if (this.originalSend) XMLHttpRequest.prototype.send = this.originalSend
    this.originalFetch = null
    this.originalSendBeacon = null
    this.originalOpen = null
    this.originalSend = null
  }

  recent(max = 12, maxAgeMs = 120000): NetworkEntry[] {
    const cutoff = Date.now() - maxAgeMs
    return this.entries
      .filter(entry => Date.parse(entry.timestamp) >= cutoff)
      .slice(-max)
  }

  private patchFetch(): void {
    if (this.originalFetch) return
    this.originalFetch = window.fetch.bind(window)
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const started = performance.now()
      const method = methodFromInput(input, init)
      const url = sanitizeUrl(urlFromInput(input))
      const requestBody = await requestBodyFromFetch(input, init)
      try {
        const response = await this.originalFetch!(input, init)
        const entry: Omit<NetworkEntry, 'timestamp'> = {
          source: 'fetch',
          method,
          url,
          status: response.status,
          durationMs: performance.now() - started,
        }
        if (shouldCaptureBodies(method, response.status)) {
          entry.requestBody = requestBody
          entry.responseBody = await responseBodyFromFetch(response)
        }
        this.add(entry)
        return response
      } catch (err) {
        this.add({
          source: 'fetch',
          method,
          url,
          durationMs: performance.now() - started,
          error: err instanceof Error ? err.message : String(err),
          requestBody,
        })
        throw err
      }
    }
  }

  private patchSendBeacon(): void {
    if (this.originalSendBeacon) return
    this.originalSendBeacon = navigator.sendBeacon.bind(navigator)
    navigator.sendBeacon = (url: string | URL, data?: BodyInit | null): boolean => {
      const started = performance.now()
      const requestUrl = sanitizeUrl(String(url))
      const requestBody = stringifyBody(data)
      const queued = this.originalSendBeacon!(url, data)
      this.add({
        source: 'fetch',
        method: 'POST',
        url: requestUrl,
        durationMs: performance.now() - started,
        requestBody,
        error: queued ? undefined : 'sendBeacon rejected',
      })
      return queued
    }
  }

  private patchXhr(): void {
    if (this.originalOpen || this.originalSend) return
    this.originalOpen = XMLHttpRequest.prototype.open
    this.originalSend = XMLHttpRequest.prototype.send
    const originalOpen = this.originalOpen
    const originalSend = this.originalSend
    const add = this.add.bind(this)

    XMLHttpRequest.prototype.open = function open(method: string, url: string | URL, ...rest: unknown[]) {
      ;(this as XMLHttpRequest & {
        __visualQARequest?: {
          method: string
          url: string
          requestBody?: string
          failure?: string
          captured?: boolean
        }
      }).__visualQARequest = {
        method: method.toUpperCase(),
        url: sanitizeUrl(String(url)),
      }
      if (rest.length === 0) return originalOpen.call(this, method, url, true)
      return originalOpen.call(this, method, url, ...(rest as [boolean, string?, string?]))
    }

    XMLHttpRequest.prototype.send = function send(body?: Document | XMLHttpRequestBodyInit | null) {
      const req = (this as XMLHttpRequest & {
        __visualQARequest?: {
          method: string
          url: string
          requestBody?: string
          failure?: string
          captured?: boolean
        }
      }).__visualQARequest
      const started = performance.now()
      if (req) req.requestBody = stringifyBody(body)
      const markFailure = (failure: string): void => {
        if (req) req.failure = failure
      }
      this.addEventListener('error', () => markFailure('network error'))
      this.addEventListener('abort', () => markFailure('request aborted'))
      this.addEventListener('timeout', () => markFailure('request timed out'))
      this.addEventListener('loadend', () => {
        if (!req || req.captured) return
        req.captured = true
        const entry: Omit<NetworkEntry, 'timestamp'> = {
          source: 'xhr',
          method: req.method,
          url: req.url,
          status: this.status,
          durationMs: performance.now() - started,
        }
        if (req.failure || this.status === 0) {
          entry.error = req.failure ?? 'request failed'
          entry.requestBody = req.requestBody
          entry.responseBody = stringifyBody(this.responseText)
        } else if (shouldCaptureBodies(req.method, this.status)) {
          entry.requestBody = req.requestBody
          entry.responseBody = stringifyBody(this.responseText)
        }
        add(entry)
      })
      return originalSend.call(this, body)
    }
  }

  private add(entry: Omit<NetworkEntry, 'timestamp'>): void {
    if (this.shouldIgnore(entry.url)) return
    this.entries.push({
      ...entry,
      durationMs: Math.round(entry.durationMs),
      timestamp: new Date().toISOString(),
    })
    if (this.entries.length > this.limit) {
      this.entries.splice(0, this.entries.length - this.limit)
    }
  }

  private shouldIgnore(input: string): boolean {
    try {
      const url = new URL(input, window.location.href)
      const endpoint = new URL(this.endpoint, window.location.href)
      return (url.origin === endpoint.origin && url.pathname.startsWith(endpoint.pathname)) ||
        url.pathname.startsWith('/@vite') ||
        url.pathname.startsWith('/@react-refresh') ||
        url.pathname.startsWith('/node_modules/.vite')
    } catch {
      return input.includes(this.endpoint)
    }
  }
}
